/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertIsInstructionWithAccounts,
  containsBytes,
  fixEncoderSize,
  getBytesEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_ACCOUNT,
  SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_INSTRUCTION,
  SOLANA_ERROR__PROGRAM_CLIENTS__UNRECOGNIZED_INSTRUCTION_TYPE,
  SolanaError,
  type Address,
  type ClientWithPayer,
  type ClientWithRpc,
  type ClientWithTransactionPlanning,
  type ClientWithTransactionSending,
  type GetAccountInfoApi,
  type GetMultipleAccountsApi,
  type Instruction,
  type InstructionWithData,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  addSelfFetchFunctions,
  addSelfPlanAndSendFunctions,
  type SelfFetchFunctions,
  type SelfPlanAndSendFunctions,
} from '@solana/program-client-core';
import {
  getLaunchCodec,
  getVestingConfigCodec,
  type Launch,
  type LaunchArgs,
  type VestingConfig,
  type VestingConfigArgs,
} from '../accounts';
import {
  getClaimInstructionAsync,
  getInitializeVestingInstructionAsync,
  parseClaimInstruction,
  parseInitializeVestingInstruction,
  type ClaimAsyncInput,
  type InitializeVestingAsyncInput,
  type ParsedClaimInstruction,
  type ParsedInitializeVestingInstruction,
} from '../instructions';

export const DOPPLER_VESTING_PROGRAM_ADDRESS =
  '2dhWRsojcuJbEJ2uaB6ayGwXz6PcFaPGpjnqP9Bawead' as Address<'2dhWRsojcuJbEJ2uaB6ayGwXz6PcFaPGpjnqP9Bawead'>;

export enum DopplerVestingAccount {
  Launch,
  VestingConfig,
}

export function identifyDopplerVestingAccount(
  account: { data: ReadonlyUint8Array } | ReadonlyUint8Array,
): DopplerVestingAccount {
  const data = 'data' in account ? account.data : account;
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([144, 51, 51, 163, 206, 85, 213, 38]),
      ),
      0,
    )
  ) {
    return DopplerVestingAccount.Launch;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([0, 138, 71, 135, 26, 29, 43, 125]),
      ),
      0,
    )
  ) {
    return DopplerVestingAccount.VestingConfig;
  }
  throw new SolanaError(
    SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_ACCOUNT,
    { accountData: data, programName: 'dopplerVesting' },
  );
}

export enum DopplerVestingInstruction {
  Claim,
  InitializeVesting,
}

export function identifyDopplerVestingInstruction(
  instruction: { data: ReadonlyUint8Array } | ReadonlyUint8Array,
): DopplerVestingInstruction {
  const data = 'data' in instruction ? instruction.data : instruction;
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([62, 198, 214, 193, 213, 159, 108, 210]),
      ),
      0,
    )
  ) {
    return DopplerVestingInstruction.Claim;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([5, 29, 245, 237, 50, 242, 35, 13]),
      ),
      0,
    )
  ) {
    return DopplerVestingInstruction.InitializeVesting;
  }
  throw new SolanaError(
    SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_INSTRUCTION,
    { instructionData: data, programName: 'dopplerVesting' },
  );
}

export type ParsedDopplerVestingInstruction<
  TProgram extends string = '2dhWRsojcuJbEJ2uaB6ayGwXz6PcFaPGpjnqP9Bawead',
> =
  | ({
      instructionType: DopplerVestingInstruction.Claim;
    } & ParsedClaimInstruction<TProgram>)
  | ({
      instructionType: DopplerVestingInstruction.InitializeVesting;
    } & ParsedInitializeVestingInstruction<TProgram>);

export function parseDopplerVestingInstruction<TProgram extends string>(
  instruction: Instruction<TProgram> & InstructionWithData<ReadonlyUint8Array>,
): ParsedDopplerVestingInstruction<TProgram> {
  const instructionType = identifyDopplerVestingInstruction(instruction);
  switch (instructionType) {
    case DopplerVestingInstruction.Claim: {
      assertIsInstructionWithAccounts(instruction);
      return {
        instructionType: DopplerVestingInstruction.Claim,
        ...parseClaimInstruction(instruction),
      };
    }
    case DopplerVestingInstruction.InitializeVesting: {
      assertIsInstructionWithAccounts(instruction);
      return {
        instructionType: DopplerVestingInstruction.InitializeVesting,
        ...parseInitializeVestingInstruction(instruction),
      };
    }
    default:
      throw new SolanaError(
        SOLANA_ERROR__PROGRAM_CLIENTS__UNRECOGNIZED_INSTRUCTION_TYPE,
        {
          instructionType: instructionType as string,
          programName: 'dopplerVesting',
        },
      );
  }
}

export type DopplerVestingPlugin = {
  accounts: DopplerVestingPluginAccounts;
  instructions: DopplerVestingPluginInstructions;
};

export type DopplerVestingPluginAccounts = {
  launch: ReturnType<typeof getLaunchCodec> &
    SelfFetchFunctions<LaunchArgs, Launch>;
  vestingConfig: ReturnType<typeof getVestingConfigCodec> &
    SelfFetchFunctions<VestingConfigArgs, VestingConfig>;
};

export type DopplerVestingPluginInstructions = {
  claim: (
    input: MakeOptional<ClaimAsyncInput, 'payer'>,
  ) => ReturnType<typeof getClaimInstructionAsync> & SelfPlanAndSendFunctions;
  initializeVesting: (
    input: MakeOptional<InitializeVestingAsyncInput, 'payer'>,
  ) => ReturnType<typeof getInitializeVestingInstructionAsync> &
    SelfPlanAndSendFunctions;
};

export type DopplerVestingPluginRequirements = ClientWithRpc<
  GetAccountInfoApi & GetMultipleAccountsApi
> &
  ClientWithPayer &
  ClientWithTransactionPlanning &
  ClientWithTransactionSending;

export function dopplerVestingProgram() {
  return <T extends DopplerVestingPluginRequirements>(client: T) => {
    return {
      ...client,
      dopplerVesting: <DopplerVestingPlugin>{
        accounts: {
          launch: addSelfFetchFunctions(client, getLaunchCodec()),
          vestingConfig: addSelfFetchFunctions(client, getVestingConfigCodec()),
        },
        instructions: {
          claim: (input) =>
            addSelfPlanAndSendFunctions(
              client,
              getClaimInstructionAsync({
                ...input,
                payer: input.payer ?? client.payer,
              }),
            ),
          initializeVesting: (input) =>
            addSelfPlanAndSendFunctions(
              client,
              getInitializeVestingInstructionAsync({
                ...input,
                payer: input.payer ?? client.payer,
              }),
            ),
        },
      },
    };
  };
}

type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
